import React from 'react';
import { ArrowRight, Star, CheckCircle2, Check } from 'lucide-react';
import { useTranslations } from '../hooks/useTranslations';
export function Hero() {
  const {
    t
  } = useTranslations();
  return <section id="about" className="relative pt-32 pb-8 lg:pt-40 lg:pb-12 overflow-hidden bg-white" data-id="element-208">
      {/* Background Pattern */}
      <div className="absolute inset-0 z-0 opacity-[0.03]" style={{
      backgroundImage: 'radial-gradient(#00E676 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-209" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-210">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-211">
          {/* Left Content */}
          <div className="space-y-6 text-center lg:text-left" data-id="element-212">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-213">
              {t('hero.title')}
            </h1>

            <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-214">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col items-center lg:items-start gap-3" data-id="element-215">
              {/* Primary CTA Button with subtle pulse animation */}
              <a href="https://form.jotform.com/260402880146048" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center justify-center text-lg font-semibold px-8 py-4 rounded-full text-white bg-[#00E676] shadow-lg shadow-green-200/50 hover:bg-[#00d169] hover:shadow-xl hover:shadow-green-300/50 hover:scale-[1.03] active:scale-[0.98] transition-all duration-300 ease-out animate-hero-pulse" data-id="element-216">
                {t('hero.cta')}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" data-id="element-217" />
              </a>

              {/* Secondary CTA Button - no pulse, subtle style */}
              <button onClick={() => document.getElementById('contact')?.scrollIntoView({
              behavior: 'smooth'
            })} className="inline-flex items-center justify-center text-sm font-semibold px-6 py-2.5 rounded-full text-[#00E676] border-2 border-[#00E676] bg-transparent hover:bg-[#00E676] hover:text-white active:scale-[0.97] transition-all duration-200 cursor-pointer" data-id="element-218">
                {t('hero.ctaSecondary')}
              </button>

              {/* Microcopy - Trust Signals */}
              <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-sm text-gray-600" data-id="element-219">
                {t('hero.microcopy1') && <div className="flex items-center gap-1.5" data-id="element-220">
                    <Check className="w-4 h-4 text-[#00E676]" data-id="element-221" />
                    <span data-id="element-222">{t('hero.microcopy1')}</span>
                  </div>}
                {t('hero.microcopy2') && <div className="flex items-center gap-1.5" data-id="element-223">
                    <Check className="w-4 h-4 text-[#00E676]" data-id="element-224" />
                    <span data-id="element-225">{t('hero.microcopy2')}</span>
                  </div>}
                {t('hero.microcopy3') && <div className="flex items-center gap-1.5" data-id="element-226">
                    <Check className="w-4 h-4 text-[#00E676]" data-id="element-227" />
                    <span data-id="element-228">{t('hero.microcopy3')}</span>
                  </div>}
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6" data-id="element-229">
              <div className="flex items-center text-sm font-medium text-gray-600" data-id="element-230">
                <div className="flex -space-x-2 mr-3" data-id="element-231">
                  {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-white bg-gray-200 overflow-hidden" data-id="element-232">
                      <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" className="w-full h-full object-cover" data-id="element-233" />
                    </div>)}
                </div>
                {t('hero.trustedBy')}
              </div>
              <div className="flex items-center gap-2" data-id="element-234">
                <div className="flex" data-id="element-235">
                  {/* 4 full stars */}
                  {[1, 2, 3, 4].map(i => <Star key={i} className="w-5 h-5 text-[#00b67a] fill-current" data-id="element-236" />)}
                  {/* 5th star: 80% filled for 4.8 rating */}
                  <div className="relative w-5 h-5" data-id="element-237">
                    <Star className="w-5 h-5 text-gray-300 fill-current absolute inset-0" data-id="element-238" />
                    <div className="absolute inset-0 overflow-hidden" style={{
                    width: '80%'
                  }} data-id="element-239">
                      <Star className="w-5 h-5 text-[#00b67a] fill-current" data-id="element-240" />
                    </div>
                  </div>
                </div>
                <span className="text-sm font-semibold text-gray-900" data-id="element-241">
                  {t('hero.trustpilot')}
                </span>
              </div>
            </div>
          </div>

          {/* Right Content - Image */}
          <div className="relative lg:h-[600px] flex items-center justify-center" data-id="element-242">
            <div className="relative w-full max-w-lg lg:max-w-none" data-id="element-243">
              <div className="relative" data-id="element-244">
                <img src="https://cdn.magicpatterns.com/uploads/kGkyYG1wwa3dggLJRfv8Sm/vecteezy_business-woman-in-glasses-using-her-phone-on-transparent_47308872.png" alt="Professional Business Executive" className="w-full h-auto object-contain relative z-10" data-id="element-245" />

                {/* Floating Approval Badge 1 */}
                <div className="absolute top-12 right-8 bg-white px-5 py-3 rounded-full shadow-xl border-2 border-[#00E676] flex items-center gap-2 animate-bounce-slow hidden md:flex" data-id="element-246">
                  <CheckCircle2 className="w-5 h-5 text-[#00E676]" data-id="element-247" />
                  <div data-id="element-248">
                    <p className="text-sm font-bold text-gray-900" data-id="element-249">$150,000</p>
                    <p className="text-xs text-gray-500" data-id="element-250">
                      {t('hero.approvalBadge1')}
                    </p>
                  </div>
                </div>

                {/* Floating Approval Badge 2 */}
                <div className="absolute top-1/3 left-4 bg-white px-5 py-3 rounded-full shadow-xl border-2 border-[#00E676] flex items-center gap-2 hidden md:flex" data-id="element-251">
                  <CheckCircle2 className="w-5 h-5 text-[#00E676]" data-id="element-252" />
                  <div data-id="element-253">
                    <p className="text-sm font-bold text-gray-900" data-id="element-254">$200,000</p>
                    <p className="text-xs text-gray-500" data-id="element-255">
                      {t('hero.approvalBadge2')}
                    </p>
                  </div>
                </div>

                {/* Floating Approval Badge 3 */}
                <div className="absolute bottom-20 right-12 bg-white px-5 py-3 rounded-full shadow-xl border-2 border-[#00E676] flex items-center gap-2 hidden lg:flex" data-id="element-256">
                  <CheckCircle2 className="w-5 h-5 text-[#00E676]" data-id="element-257" />
                  <div data-id="element-258">
                    <p className="text-sm font-bold text-gray-900" data-id="element-259">$75,000</p>
                    <p className="text-xs text-gray-500" data-id="element-260">
                      {t('hero.approvalBadge3')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Decorative blob */}
              <div className="absolute -z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-green-100 to-emerald-50 rounded-full blur-3xl opacity-40" data-id="element-261"></div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}